/**
 * MindMapSidebar Component
 * Draggable node palette for creating new nodes
 */

import React from "react";
import { Box, Typography, Paper } from "@mui/material";
import CircleIcon from "@mui/icons-material/Circle";
import RoundedCornerIcon from "@mui/icons-material/RoundedCorner";
import CropSquareIcon from "@mui/icons-material/CropSquare";
import FlagIcon from "@mui/icons-material/Flag";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import CategoryIcon from "@mui/icons-material/Category";
import { useColorMode } from "../../../contexts/AppThemeProvider";
import { useDnD } from "./DnDContext";
import { MindMapNodeType } from "./mindMapTypes";

interface NodeTypeConfig {
  type: MindMapNodeType;
  label: string;
  icon: React.ReactNode;
  color: string;
  darkColor: string;
}

const nodeTypes: NodeTypeConfig[] = [
  {
    type: "topic",
    label: "Topic",
    icon: <CircleIcon />,
    color: "#2bb39d",
    darkColor: "#5dc4b2",
  },
  {
    type: "idea",
    label: "Idea",
    icon: <RoundedCornerIcon />,
    color: "#4f6ef7",
    darkColor: "#6b8afd",
  },
  {
    type: "note",
    label: "Note",
    icon: <CropSquareIcon />,
    color: "#d97706",
    darkColor: "#f59e0b",
  },
  {
    type: "goal",
    label: "Goal",
    icon: <FlagIcon />,
    color: "#ea580c",
    darkColor: "#f97316",
  },
  {
    type: "step",
    label: "Step",
    icon: <FormatListNumberedIcon />,
    color: "#eab308",
    darkColor: "#facc15",
  },
  {
    type: "category",
    label: "Category",
    icon: <CategoryIcon />,
    color: "#7c3aed",
    darkColor: "#a855f7",
  },
];

const MindMapSidebar: React.FC = () => {
  const { mode } = useColorMode();
  const [, setType] = useDnD();
  const isDark = mode === "dark";

  const onDragStart = (
    event: React.DragEvent<HTMLDivElement>,
    nodeType: MindMapNodeType
  ) => {
    setType(nodeType);
    event.dataTransfer.setData("application/reactflow", nodeType);
    event.dataTransfer.effectAllowed = "move";
  };

  return (
    <Box
      sx={{
        width: 120,
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        borderRight: 1,
        borderColor: isDark ? "rgba(255,255,255,0.1)" : "divider",
        bgcolor: isDark ? "#1a1a1a" : "#fafafa",
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}
      >
        Drag to add
      </Typography>

      {nodeTypes.map((node) => (
        <Paper
          key={node.type}
          draggable
          onDragStart={(e) => onDragStart(e, node.type)}
          elevation={0}
          sx={{
            p: 1.5,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 0.5,
            cursor: "grab",
            border: 1,
            borderColor: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)",
            borderRadius: 2,
            transition: "all 0.2s ease",
            "&:hover": {
              borderColor: isDark ? node.darkColor : node.color,
              bgcolor: isDark ? `${node.darkColor}20` : `${node.color}10`,
              transform: "translateY(-2px)",
            },
            "&:active": {
              cursor: "grabbing",
            },
          }}
        >
          <Box
            sx={{
              color: isDark ? node.darkColor : node.color,
              display: "flex",
            }}
          >
            {node.icon}
          </Box>
          <Typography variant="caption" sx={{ fontWeight: 500 }}>
            {node.label}
          </Typography>
        </Paper>
      ))}

      <Box sx={{ mt: "auto", pt: 2 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ lineHeight: 1.4, display: "block" }}
        >
          Double-click nodes to edit. Drag handles to connect.
        </Typography>
      </Box>
    </Box>
  );
};

export default MindMapSidebar;
